import React, { useEffect, useRef, useCallback } from 'react';
import './ConfirmationModal.css';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverMessageStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle,
  getDeleteButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared Confirmation Modal Component
 * Used for delete confirmations, mark as sold, and other confirmation dialogs
 * Issue #115: Full keyboard accessibility support
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close/cancel callback
 * @param {Function} onConfirm - Confirm action callback
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Text for confirm button
 * @param {string} confirmingText - Text shown while confirming
 * @param {boolean} isConfirming - Whether action is in progress
 * @param {string} variant - 'default' | 'danger' - Button style variant
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmingText,
  cancelText,
  isConfirming = false,
  variant = 'default'
}) => {
  const { t } = useLanguage();
  const modalRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const confirmButtonRef = useRef(null);

  // Focus first button when modal opens
  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 10);
    }
  }, [isOpen]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape' && !isConfirming) {
      e.preventDefault();
      onClose();
      return;
    }

    // Focus trap - Tab and Shift+Tab
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, isConfirming, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const getConfirmButtonStyle = variant === 'danger' ? getDeleteButtonStyle : getSubmitButtonStyle;

  return (
    <div
      className="modal-overlay"
      onClick={isConfirming ? undefined : onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={popoverContainerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
        tabIndex={-1}
      >
        <h2 id="confirmation-modal-title" style={popoverHeaderStyle}>{title}</h2>
        <p style={popoverMessageStyle}>{message}</p>
        <div style={popoverButtonContainerStyle}>
          <button
            ref={cancelButtonRef}
            type="button"
            className="confirmation-modal-button"
            onClick={onClose}
            disabled={isConfirming}
            style={getCancelButtonStyle(isConfirming)}
          >
            {cancelText || t('cancel')}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            className="confirmation-modal-button"
            onClick={onConfirm}
            disabled={isConfirming}
            style={getConfirmButtonStyle(isConfirming)}
          >
            {isConfirming ? confirmingText : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
